// types.ts - TypeScript type definitions for NOVA Chat Widget

export interface NovaWidgetConfig {
  /** Your Nova widget API key (required) */
  apiKey: string;

  /** Widget position on screen */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  /** Theme preset */
  theme?: 'cosmic' | 'light' | 'dark' | 'auto';

  /** Custom theme colors (overrides preset) */
  colors?: {
    primary?: string;
    background?: string;
    text?: string;
    accent?: string;
  };

  /** Initial greeting message from Nova */
  greeting?: string;

  /**
   * Example questions shown as one-tap chips until the visitor sends a first
   * message. Defaults to DEFAULT_STARTER_PROMPTS; pass [] to hide them.
   */
  starterPrompts?: string[];

  /** Placeholder text in input field */
  placeholder?: string;

  /** Widget title shown in header */
  title?: string;

  /**
   * Your own picture for the assistant (http(s) URL). When set, it replaces Nova's
   * animated face everywhere in the widget (launcher, header, typing indicator,
   * welcome) and does not change with the conversation.
   */
  avatarUrl?: string;

  /** Unique visitor identifier for session tracking */
  visitorId?: string;

  /** Optional username for the visitor */
  username?: string;

  /** Z-index for the widget */
  zIndex?: number;

  /** Whether widget starts open */
  defaultOpen?: boolean;

  /** Enable/disable features */
  features?: {
    /** Show message history from previous sessions */
    history?: boolean;
    /** Enable feedback buttons on messages */
    feedback?: boolean;
    /** Allow account linking (Discord/Telegram) */
    accountLinking?: boolean;
    /** Show typing indicator */
    typingIndicator?: boolean;
    /** Enable sound notifications */
    sounds?: boolean;
  };

  /** API endpoint (defaults to production) */
  apiUrl?: string;

  /** URL for premium upgrade (opens in new tab when user clicks upgrade) */
  upgradeUrl?: string;

  /** Callback when widget opens */
  onOpen?: () => void;

  /** Callback when widget closes */
  onClose?: () => void;

  /** Callback when message is sent */
  onMessageSent?: (message: string) => void;

  /** Callback when response is received */
  onMessageReceived?: (message: NovaMessage) => void;

  /** Callback on errors */
  onError?: (error: NovaError) => void;

  /** Callback when session is ready */
  onReady?: (session: NovaSession) => void;
}

export interface NovaMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  feedback?: 'thumbs_up' | 'thumbs_down' | null;
  /** Follow-up questions offered as one-tap chips under the latest answer */
  suggestions?: string[];
}

export interface NovaSession {
  sessionId: string;
  siteId: string;
  siteName?: string;
  isNew: boolean;
  linkedUser?: {
    userId: string;
    platform: 'discord' | 'telegram' | 'google' | 'stacks' | 'bitcoin';
  } | null;
}

export interface NovaError {
  code: string;
  message: string;
  retryAfterMs?: number;
}

export interface NovaWidgetAPI {
  /** Open the chat widget */
  open: () => void;

  /** Close the chat widget */
  close: () => void;

  /** Toggle widget open/closed */
  toggle: () => void;

  /** Send a message programmatically */
  sendMessage: (content: string) => Promise<void>;

  /** Get current session info */
  getSession: () => NovaSession | null;

  /** Get message history */
  getMessages: () => NovaMessage[];

  /** Clear conversation history */
  clearHistory: () => void;

  /** Update configuration */
  updateConfig: (config: Partial<NovaWidgetConfig>) => void;

  /** Destroy the widget instance */
  destroy: () => void;

  /** Check if widget is open */
  isOpen: () => boolean;
}

// Default starter chips. They mirror STARTER_PROMPTS in the bot's
// services/onboarding/helpContent.js (the widget is a separate bundle and
// cannot import it): keep both halves of Nova's scope, blockchain and
// community, and only questions an anonymous free visitor can get answered.
export const DEFAULT_STARTER_PROMPTS: string[] = [
  "What's the price of Bitcoin and STX right now?",
  'What are the current Bitcoin network fees?',
  'Show me open bounties and grants',
  'What can you do for my community?',
];

// Default configuration values
export const DEFAULT_CONFIG: Required<Pick<NovaWidgetConfig,
  'position' | 'theme' | 'greeting' | 'placeholder' | 'title' | 'zIndex' | 'defaultOpen' | 'apiUrl'
>> & { features: Required<NonNullable<NovaWidgetConfig['features']>> } = {
  position: 'bottom-right',
  theme: 'cosmic',
  greeting: 'Hello! I\'m Nova, your AI assistant. How can I help you today?',
  placeholder: 'Type your message...',
  title: 'Nova',
  zIndex: 999999,
  defaultOpen: false,
  apiUrl: 'https://nova.zeroauthoritydao.com',
  features: {
    history: true,
    feedback: true,
    accountLinking: true,
    typingIndicator: true,
    sounds: false,
  },
};
